const AppPageName = require('@/lib/Navigation/AppPageName');
const PrivilegeNames = require('@/lib/Privileges/PrivilegeNames');

/**
 * @constructor
 *
 * @param {PrivilegesManager} privilegesManager
 * @param {NavigationService} navigationService
 */
const LandingPageService = function (
    privilegesManager,
    navigationService
) {
    this.privilegesManager = privilegesManager;
    this.navigationService = navigationService;

    this.landingPageCode = null;

    // Information about the EPOS landing page(s).
    this.pageInfo = {
        isParsed: false,
        topLevel: null,
        topLevelPath: null,
        secondary: null,
    };

    this.validTopLevels = [
        AppPageName.ACCOUNTS,
        AppPageName.ORDERS,
        AppPageName.REPORT,
        AppPageName.SELL,
        AppPageName.TODAY,
    ];
};

LandingPageService.prototype = {

    //
    // Method to decompose and interpret the landing page code. It puts the various
    // components into properties of the object. It only does it if it hasn't already
    // done it.
    //
    // @param pageCode
    //
    parseLandingCode: function parseLandingCode(pageCode) {
        if (this.landingPageCode !== pageCode) {
            this.landingPageCode = pageCode;
            this.pageInfo.isParsed = false;
        }
        if (!this.pageInfo.isParsed) {
            // Reset everything.
            this.pageInfo.topLevel = null;
            this.pageInfo.topLevelPath = null;
            this.pageInfo.secondary = null;
            // The code is of the form <top-level-page>[.<secondary-tab>].
            // Split on the dot to get the top-level page and optional secondary tab.
            // Actually the code could be null, in which case we assume '/sell'.
            let codeParts = this.landingPageCode == null ? [] : this.landingPageCode.split('.');
            // Make sure the top-level code is valid.
            if (codeParts.length < 1 || this.validTopLevels.indexOf(codeParts[0]) === -1) {
                // Empty or not valid. Assume sell with no secondary.
                codeParts = ['sell'];
            }
            this.pageInfo.topLevel = codeParts[0];
            this.pageInfo.topLevelPath = '/' + this.pageInfo.topLevel;
            // See if there's a secondary tab. Default to 'tickets'.
            if (codeParts.length === 2) {
                this.pageInfo.secondary = codeParts[1];
            } else {
                this.pageInfo.secondary = this.pageInfo.topLevel === AppPageName.SELL ? 'tickets' : null;
            }
            // Make a note that we've parsed the code.
            this.pageInfo.isParsed = true;
        }
    },

    /**
     * Method to return the top-level path.
     *
     * @returns {string|null} The path, eg '/sell' or '/report'.
     * @param pageCode
     * @param callback
     */
    getTopLevelScreenPath: function getTopLevelScreenPath(pageCode, callback) {
        let self = this;

        // See if we've seen this code before, and if so, whether we've parsed it.
        this.parseLandingCode(pageCode);

        // Verify we can access that top level.
        let privilegeMappings = {
            // todo you cannot have duplicate keys, DOJO_REPORT does not work?
            [PrivilegeNames.ACCESS_ACCOUNTS]: AppPageName.ACCOUNTS,
            [PrivilegeNames.ACCESS_REDEEM]: AppPageName.ORDERS,
            [PrivilegeNames.ACCESS_REPORT]: AppPageName.DOJO_REPORT,
            [PrivilegeNames.ACCESS_REPORT]: AppPageName.REPORT,
            [PrivilegeNames.ACCESS_SELL]: AppPageName.SELL,
            [PrivilegeNames.ACCESS_TODAY]: AppPageName.TODAY,
        };

        let access = [];
        this.privilegesManager.checkMultipleCurrentUserPrivileges(
            Object.keys(privilegeMappings)
        ).then(
            /**
             * @param {PrivilegeCheckResult[]} results
             */
            function (results) {
                // eslint-disable-next-line unused-imports/no-unused-vars
                for (let privilegeName in results) {
                    if (results.hasOwnProperty(privilegeName)) {
                        let result = results[privilegeName];
                        if (result.granted) {
                            let page = privilegeMappings[result.privilegeName];
                            access.push(page);
                        }
                    }
                }

                if (access.indexOf(self.pageInfo.topLevel) === -1) {
                    // no access! access array should contain only valid locations, so we can force reload to first one.
                    if (access.length < 1) {
                        // no access!
                        console.log('LandingPageService.getTopLevelScreenPath: user does not have access to ' + pageCode + ', no alternative pages found');
                        // this needs to logout not just redirect - debate? 403 is invalid a
                        self.navigationService.showNav();
                        callback('/403');
                        return;
                    }
                    // load a top level for something they do have access to!
                    console.log('LandingPageService.getTopLevelScreenPath: user does not have access to ' + pageCode + ', redirecting to ' + access[0]);
                    self.parseLandingCode(access[0]);
                }

                // Return what we've determined to be the top-level path.
                callback(self.pageInfo.topLevelPath);
            }
        );
    },
};

module.exports = LandingPageService;
